import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { loadThemePreference, saveThemePreference } from '../utils/localStorage';

/** Supported color themes. Light is the default. */
export type Theme = 'light' | 'dark';

/**
 * Theme context value providing the active theme and mutators.
 */
interface ThemeContextValue {
  /** The currently active theme. */
  theme: Theme;
  /** Toggle between light and dark. */
  toggleTheme: () => void;
  /** Set the theme explicitly. */
  setTheme: (theme: Theme) => void;
}

const DEFAULT_THEME: Theme = 'light';

/**
 * Applies the theme to the document so CSS variables under `[data-theme="dark"]`
 * take effect. Centralized so the provider and the safe default stay consistent.
 */
function applyThemeAttribute(theme: Theme): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme);
  }
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * ThemeProvider - owns the active theme, persists the user's choice, and writes
 * the `data-theme` attribute on the document element. CSS custom properties do
 * the rest, so descendants do not need to consume colors through context.
 *
 * Light is the default; first-time visitors always start light (we intentionally
 * do not follow `prefers-color-scheme`). A small inline script in `index.html`
 * sets the attribute before first paint to avoid a flash for returning dark users.
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Lazy initializer reads persisted choice synchronously so the first React
  // render agrees with the attribute the FOUC script already set pre-paint.
  const [theme, setThemeState] = useState<Theme>(() => loadThemePreference() ?? DEFAULT_THEME);

  // Keep the DOM attribute and persisted value in sync with state. Idempotent,
  // so React StrictMode's double-invoke in development is harmless.
  useEffect(() => {
    applyThemeAttribute(theme);
    saveThemePreference(theme);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState(prev => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const value: ThemeContextValue = { theme, toggleTheme, setTheme };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

/**
 * Hook to access the active theme.
 *
 * Unlike most context hooks here, this does NOT throw when used outside a
 * provider — it returns a safe light-mode default. Many leaf components (nodes,
 * progress bars, the minimap) read the theme, and they are rendered directly in
 * unit tests without a provider; throwing would force every such test to add a
 * wrapper. The safe default keeps those tests working and degrades gracefully.
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    return { theme: DEFAULT_THEME, toggleTheme: () => {}, setTheme: () => {} };
  }
  return context;
}
